import React, { Component, createRef } from 'react';

// CSS
import '../Subscriptions/Subscription.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Alert } from '../../components/UI/Feedback';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

// Properties
const properties = require('../../properties.json');

// the fields, in the order they are read -- each one knows its own name,
// its example and (for a social account) the mark that goes beside it
const CONTACT = [
    { key: 'email', label: 'البريد الإلكتروني للتواصل', icon: 'pi pi-envelope', placeholder: 'example@email.com' },
    { key: 'mobile', label: 'رقم الموبايل للتواصل', icon: 'pi pi-phone', placeholder: '01xxxxxxxxx' },
    { key: 'location', label: 'الموقع (المدينة/الدولة)', icon: 'pi pi-map-marker', placeholder: 'مثال: المنصورة، مصر', wide: true },
];

const SOCIAL = [
    { key: 'facebookUrl', label: 'فيسبوك', icon: 'pi pi-facebook', placeholder: 'https://facebook.com/...' },
    { key: 'instagramUrl', label: 'إنستجرام', icon: 'pi pi-instagram', placeholder: 'https://instagram.com/...' },
    { key: 'linkedinUrl', label: 'لينكد إن', icon: 'pi pi-linkedin', placeholder: 'https://linkedin.com/in/...' },
    { key: 'behanceUrl', label: 'بيهانس', icon: 'pi pi-palette', placeholder: 'https://behance.net/...' },
];

const FIELDS = [...CONTACT, ...SOCIAL].map((f) => f.key);

class ContactInfo extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        email: '',
        mobile: '',
        location: '',
        facebookUrl: '',
        instagramUrl: '',
        linkedinUrl: '',
        behanceUrl: '',
        saving: false,
        loading: true,

        // what the server last gave us, so the save can stay quiet until
        // something has actually changed
        saved: {},
    }

    componentDidMount() {
        document.title = properties.site_name + " | معلومات التواصل";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        apiFetch("/teachers/me")
            .then(me => {
                const values = {
                    email: me.email || '',
                    mobile: me.mobile || '',
                    location: me.location || '',
                    facebookUrl: me.facebookUrl || '',
                    instagramUrl: me.instagramUrl || '',
                    linkedinUrl: me.linkedinUrl || '',
                    behanceUrl: me.behanceUrl || '',
                };
                this.setState({ ...values, saved: values, loading: false });
            })
            .catch(error => {
                this.setState({ loading: false });
                console.error('Error:', error);
            });
    }

    save = () => {
        const { email, mobile, location, facebookUrl, instagramUrl, linkedinUrl, behanceUrl } = this.state;
        this.setState({ saving: true });
        apiFetch("/teachers/me/contact-info", { method: "PUT", body: { email, mobile, location, facebookUrl, instagramUrl, linkedinUrl, behanceUrl } })
            .then(() => {
                this.setState({
                    saving: false,
                    saved: { email, mobile, location, facebookUrl, instagramUrl, linkedinUrl, behanceUrl },
                });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم حفظ معلومات التواصل' });
            })
            .catch(error => {
                this.setState({ saving: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    render() {
        const { saving, loading, saved } = this.state;

        const dirty = FIELDS.some((k) => (this.state[k] || '') !== (saved[k] || ''));
        const shown = SOCIAL.filter((s) => String(this.state[s.key] || '').trim()).length;

        const field = (f) => (
            <div className={f.wide ? "f is-wide" : "f"} key={f.key}>
                <span className="p-float-label">
                    <InputText
                        id={f.key}
                        value={this.state[f.key]}
                        placeholder={f.placeholder}
                        onChange={(e) => this.setState({ [f.key]: e.target.value })}
                    />
                    <label htmlFor={f.key}>
                        <i className={f.icon}></i>
                        {f.label}
                    </label>
                </span>
            </div>
        );

        return (
            <>
                <Header
                    pageName="معلومات التواصل"
                    action={{
                        label: saving ? 'جاري الحفظ…' : 'حفظ التغييرات',
                        icon: saving ? 'pi pi-spin pi-spinner' : 'pi pi-check',
                        disabled: saving || !dirty,
                        onClick: this.save,
                    }}
                />
                <Toast ref={this.toast}></Toast>
                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                {/* the form is laid out underneath; the veil covers it
                                    until the teacher's own data has arrived */}
                                {loading ? (
                                    <div className="ui-form-wait" role="status">
                                        <span className="spin" aria-hidden="true"></span>
                                        <b>جاري تحميل بيانات التواصل…</b>
                                    </div>
                                ) : null}

                                <section className="ui-sec">
                                    <h3>
                                        <span className="n">١</span>
                                        <span className="t">
                                            بيانات التواصل
                                            <small>اللي هيظهر للطلاب في صفحة «تواصل معنا» على موقعك</small>
                                        </span>
                                    </h3>

                                    <Alert kind="info" title="مش بيانات الدخول">
                                        دي بيانات العرض على الموقع، وممكن تكون مختلفة عن البريد والموبايل
                                        اللي بتسجّل بيهم دخولك.
                                    </Alert>

                                    <div className="ui-fields">{CONTACT.map(field)}</div>
                                </section>

                                <section className="ui-sec">
                                    <h3>
                                        <span className="n">٢</span>
                                        <span className="t">
                                            حسابات التواصل
                                            <small>
                                                {shown
                                                    ? `${shown} من ٤ أيقونات هتظهر في صفحة التواصل`
                                                    : 'سيب أي رابط فاضي ومش هتظهر أيقونته على الموقع'}
                                            </small>
                                        </span>
                                    </h3>

                                    <div className="ui-fields">{SOCIAL.map(field)}</div>
                                </section>

                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default ContactInfo;
